import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { errMsg } from '../utils/apiError';
import BookmarkButton from '../components/BookmarkButton';
import {
  ArrowLeft,
  Trophy,
  Plus,
  Heart,
  Home,
  Target,
  Handshake,
  TrendingUp,
  PartyPopper,
  Users,
  MessageCircle,
  Send,
  ChevronDown,
  ChevronUp,
  Trash2
} from 'lucide-react';

const VictoriesPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  const [victories, setVictories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [newVictory, setNewVictory] = useState({
    title: '',
    description: '',
    victory_type: 'general'
  });

  const victoryTypes = [
    { value: 'general', label: 'Victoire générale', icon: <Trophy className="w-4 h-4" /> },
    { value: 'first_property', label: 'Premier bien acquis', icon: <Home className="w-4 h-4" /> },
    { value: 'negotiation', label: 'Négociation réussie', icon: <Handshake className="w-4 h-4" /> },
    { value: 'milestone', label: 'Étape franchie', icon: <Target className="w-4 h-4" /> },
    { value: 'rental', label: 'Première location', icon: <TrendingUp className="w-4 h-4" /> }
  ];

  useEffect(() => {
    fetchVictories();
  }, []);

  const fetchVictories = async () => {
    try {
      const response = await api.get('/victories');
      setVictories(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVictory = async (e) => {
    e.preventDefault();
    if (!newVictory.title || !newVictory.description) {
      toast.error('Remplissez tous les champs');
      return;
    }
    try {
      await api.post('/victories', newVictory);
      toast.success('🏆 Victoire partagée avec l\'équipage !');
      setCreateOpen(false);
      setNewVictory({ title: '', description: '', victory_type: 'general' });
      fetchVictories();
    } catch (error) {
      toast.error(errMsg(error, 'Erreur'));
    }
  };

  const handleCongratulate = async (victoryId) => {
    try {
      await api.post(`/victories/${victoryId}/congratulate`);
      toast.success('🎉 Félicitations envoyées !');
      fetchVictories();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Tu as déjà félicité cette victoire');
      } else {
        toast.error('Erreur');
      }
    }
  };

  const handleComment = async (victoryId) => {
    const content = commentInputs[victoryId];
    if (!content?.trim()) {
      toast.error('Écris ton commentaire');
      return;
    }
    try {
      await api.post(`/victories/${victoryId}/comment`, { content });
      toast.success('💬 Commentaire ajouté !');
      setCommentInputs({ ...commentInputs, [victoryId]: '' });
      fetchVictories();
    } catch (error) {
      toast.error(errMsg(error, 'Erreur'));
    }
  };

  const toggleComments = (victoryId) => {
    setExpandedComments({ ...expandedComments, [victoryId]: !expandedComments[victoryId] });
  };

  const getVictoryIcon = (type) => {
    const found = victoryTypes.find(v => v.value === type);
    return found?.icon || <Trophy className="w-5 h-5" />;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Trophy className="w-12 h-12 text-primary animate-bounce mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement des victoires...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="victories-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-base sm:text-xl">Victoires de l'Équipage</h1>
                <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Célébrons ensemble chaque succès</p>
              </div>
            </div>
          </div>

          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-yellow-500 text-black hover:bg-yellow-400 shrink-0" data-testid="share-victory-btn">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Partager ma victoire</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <PartyPopper className="w-5 h-5 text-yellow-500" />
                  Partager une victoire
                </DialogTitle>
                <DialogDescription>Partagez votre succès avec toute la communauté !</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateVictory} className="space-y-4">
                <div className="space-y-2">
                  <Label>Type de victoire</Label>
                  <Select 
                    value={newVictory.victory_type} 
                    onValueChange={(v) => setNewVictory({ ...newVictory, victory_type: v })}
                  >
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {victoryTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            {type.icon}
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Titre de ta victoire *</Label>
                  <Input 
                    value={newVictory.title} 
                    onChange={(e) => setNewVictory({ ...newVictory, title: e.target.value })} 
                    placeholder="Ex: J'ai signé mon premier achat !" 
                    className="bg-secondary/50"
                    data-testid="victory-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Raconte-nous ! *</Label>
                  <Textarea 
                    value={newVictory.description} 
                    onChange={(e) => setNewVictory({ ...newVictory, description: e.target.value })} 
                    placeholder="Détaille ta victoire pour inspirer les autres..."
                    className="bg-secondary/50"
                    rows={4}
                    data-testid="victory-description-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                  <Button type="submit" className="bg-yellow-500 text-black hover:bg-yellow-400" data-testid="submit-victory-btn">
                    <Trophy className="w-4 h-4 mr-2" />
                    Partager
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {victories.length === 0 ? (
          <Card className="card-base text-center py-12">
            <CardContent>
              <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">Aucune victoire encore</h3>
              <p className="text-muted-foreground mb-4">Sois le premier à partager ta victoire avec l'équipage !</p>
              <Button onClick={() => setCreateOpen(true)} className="bg-yellow-500 text-black hover:bg-yellow-400">
                <Plus className="w-4 h-4 mr-2" />
                Partager ma victoire
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {victories.map((victory) => (
              <Card key={victory.id} className="card-base overflow-hidden relative" data-testid={`victory-${victory.id}`}>
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-yellow-500 to-yellow-600" />
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-12 w-12 border-2 border-yellow-500/30">
                      <AvatarImage src={victory.user_avatar} />
                      <AvatarFallback className="bg-yellow-500/20 text-yellow-500">
                        {getInitials(victory.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{victory.user_name}</span>
                        <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                          {getVictoryIcon(victory.victory_type)}
                          <span className="ml-1">{victoryTypes.find(v => v.value === victory.victory_type)?.label || 'Victoire'}</span>
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{victory.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground whitespace-pre-wrap">{victory.description}</p>
                  
                  {/* Actions Row */}
                  <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-border">
                    <div className="flex items-center gap-3 sm:gap-4 flex-wrap text-sm text-muted-foreground">
                      <button 
                        className="flex items-center gap-1 hover:text-red-500 transition-colors"
                        onClick={() => handleCongratulate(victory.id)}
                        data-testid={`congrats-btn-${victory.id}`}
                      >
                        <Heart className={`w-4 h-4 ${victory.congratulations?.includes(user.id) ? 'fill-red-500 text-red-500' : ''}`} />
                        <span>{victory.congratulations?.length || 0}</span>
                      </button>
                      <button 
                        className="flex items-center gap-1 hover:text-blue-500 transition-colors"
                        onClick={() => toggleComments(victory.id)}
                        data-testid={`toggle-comments-${victory.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span>{victory.comments?.length || 0}</span>
                        {expandedComments[victory.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                      <span className="text-xs">
                        {new Date(victory.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>
                      <BookmarkButton
                        itemType="victory"
                        itemId={victory.id}
                        itemTitle={victory.title}
                        itemPreview={victory.description?.slice(0, 200)}
                        itemUrl="/victories"
                      />
                      {(user.role === 'admin' || victory.user_id === user.id) && (
                        <button
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-500 transition-colors ml-2"
                          onClick={async () => {
                            if (window.confirm('Supprimer cette victoire ?')) {
                              try {
                                await api.delete(`/victories/${victory.id}`);
                                toast.success('Victoire supprimée');
                                fetchVictories();
                              } catch (e) { toast.error('Erreur'); }
                            }
                          }}
                          data-testid={`delete-victory-${victory.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Supprimer
                        </button>
                      )}
                    </div>
                    
                    <Button 
                      variant={victory.congratulations?.includes(user.id) ? "secondary" : "outline"}
                      size="sm"
                      onClick={() => handleCongratulate(victory.id)}
                      className={victory.congratulations?.includes(user.id) ? "bg-red-500/10 text-red-500 hover:bg-red-500/20" : "hover:bg-red-500/10 hover:text-red-500"}
                    >
                      <Heart className={`w-4 h-4 mr-1 ${victory.congratulations?.includes(user.id) ? 'fill-current' : ''}`} />
                      {victory.congratulations?.includes(user.id) ? 'Félicité !' : 'Féliciter'}
                    </Button>
                  </div>

                  {/* Comments Section */}
                  {expandedComments[victory.id] && (
                    <div className="space-y-4 pt-4 border-t border-border animate-in slide-in-from-top-2">
                      {/* Existing Comments */}
                      {victory.comments?.length > 0 && (
                        <div className="space-y-3">
                          {victory.comments.map((comment) => (
                            <div key={comment.id} className="flex gap-3" data-testid={`comment-${comment.id}`}>
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={comment.user_avatar} />
                                <AvatarFallback className="text-xs bg-secondary">
                                  {getInitials(comment.user_name)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 bg-secondary/30 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-medium">{comment.user_name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    {formatDate(comment.created_at)}
                                  </span>
                                </div>
                                <p className="text-sm">{comment.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Comment Input */}
                      <div className="flex gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatar_url} />
                          <AvatarFallback className="text-xs bg-primary/20 text-primary">
                            {getInitials(`${user.first_name} ${user.last_name}`)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 flex gap-2">
                          <Input 
                            value={commentInputs[victory.id] || ''}
                            onChange={(e) => setCommentInputs({ ...commentInputs, [victory.id]: e.target.value })}
                            placeholder="Écris un commentaire d'encouragement..."
                            className="bg-secondary/30"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleComment(victory.id);
                              }
                            }}
                            data-testid={`comment-input-${victory.id}`}
                          />
                          <Button 
                            size="icon" 
                            onClick={() => handleComment(victory.id)}
                            className="bg-yellow-500 text-black hover:bg-yellow-400"
                            data-testid={`send-comment-${victory.id}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default VictoriesPage;
